import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import "../assets/css/Signin.scss";
import { LoginLogo } from "../assets/icons";
import ButtonLoader from "../atoms/buttonLoader";
import { resetPassword } from "../services/service";
import { CustomToast } from "../atoms/toastMessage";
import buildLoginBg from "../assets/images/dashboard/buildloginbg.svg";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const token = location.state?.token;
  
  const [isLoading, setLoading] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [confirmPasswordType, setConfirmPasswordType] = useState("password");

  // Redirect if no email or token is provided
  useEffect(() => {
    if (!email || !token) {
      navigate("/forgotpassword");
    }
  }, [email, token, navigate]);

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const togglePassword = () => {
    setPasswordType((prev) => (prev === "password" ? "text" : "password"));
  };

  const toggleConfirmPassword = () => {
    setConfirmPasswordType((prev) => (prev === "password" ? "text" : "password"));
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      const response = await resetPassword({
        email,
        newPassword: values.newPassword,
      });

      if (response) {
        CustomToast({
          type: "success",
          message: "Password reset successfully",
        });
        resetForm();
        setTimeout(() => {
          navigate("/signin");
        }, 1500);
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to reset password";
      CustomToast({
        type: "error",
        message: errorMessage,
      });
    }
    setLoading(false);
    setSubmitting(false);
  };

  return (
    <div className="fluid-container">
      <div className="sign-page" style={{ backgroundImage: `url(${buildLoginBg})` }}>
        <div className="sign-page-background d-flex align-items-center">
          <div
            className="row m-0 sign-page-background-row"
            style={{ justifyContent: "center", alignItems: "center" }}
          >
            <div className="custome_offset px-lg-5 mahzen-logo d-flex justify-content-center flex-column">
              <div style={{ height: "100%", width: "100%" }}>
                <div className="row mx-lg-5 px-lg-5 pt-3 pb-2 siginup-inner py-5">
                  <div className="w-full d-flex justify-content-center">
                    <div className="login-logo d-flex justify-content-center align-items-end">
                      {LoginLogo}
                    </div>
                  </div>
                  <div className="col-12 mb-3 d-flex justify-content-center">
                    <p className="mb-0 text-[#1E3A8A] font-semibold text-[28px]">
                      Reset Password
                    </p>
                  </div>
                  <div className="col-12 mb-3 d-flex justify-content-center text-center">
                    <p className="mb-0 text-[#64748B] text-[14px]">
                      Enter your new password
                    </p>
                  </div>
                  <Formik
                    initialValues={{
                      newPassword: "",
                      confirmPassword: "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form className="w-100">
                        <div className="col-12 mt-4 mb-1 signup-input-label">
                          <div
                            className="d-flex border_custome"
                            style={{ height: "54px", borderRadius: "8px" }}
                          >
                            <Field
                              type={passwordType}
                              name="newPassword"
                              placeholder="New Password"
                              className="form-control pl-3"
                              style={{
                                border: "none",
                                backgroundColor: "transparent",
                              }}
                            />
                            <div className="loginPasswordPositionBottom input-group-btn">
                              <span
                                className="eyeBtn pr-3"
                                onClick={togglePassword}
                                style={{ cursor: "pointer" }}
                              >
                                {passwordType === "password" ? (
                                  <FaEyeSlash color="#4B5563" size={16} />
                                ) : (
                                  <FaEye color="#4B5563" size={16} />
                                )}
                              </span>
                            </div>
                          </div>
                          <ErrorMessage
                            name="newPassword"
                            component="p"
                            className="signup-staric-color"
                          />
                        </div>

                        <div className="col-12 mt-4 mb-1 signup-input-label">
                          <div
                            className="d-flex border_custome"
                            style={{ height: "54px", borderRadius: "8px" }}
                          >
                            <Field
                              type={confirmPasswordType}
                              name="confirmPassword"
                              placeholder="Confirm Password"
                              className="form-control pl-3"
                              style={{
                                border: "none",
                                backgroundColor: "transparent",
                              }}
                            />
                            <div className="loginPasswordPositionBottom input-group-btn">
                              <span
                                className="eyeBtn pr-3"
                                onClick={toggleConfirmPassword}
                                style={{ cursor: "pointer" }}
                              >
                                {confirmPasswordType === "password" ? (
                                  <FaEyeSlash color="#4B5563" size={16} />
                                ) : (
                                  <FaEye color="#4B5563" size={16} />
                                )}
                              </span>
                            </div>
                          </div>
                          <ErrorMessage
                            name="confirmPassword"
                            component="p"
                            className="signup-staric-color"
                          />
                        </div>

                        <div className="w-full flex justify-center items-center">
                          <button
                            type="submit"
                            className="w-[94.5%] bg-[#F97316] h-[52px] rounded-[4px] mt-6 flex items-center justify-center"
                            disabled={isSubmitting || isLoading}
                          >
                            {isLoading ? (
                              <ButtonLoader color="#FFFFFF" />
                            ) : (
                              <p className="font-semibold text-[20px] text-white">
                                Reset Password
                              </p>
                            )}
                          </button>
                        </div>

                        <div className="col-12 mt-4 mb-2 text-center">
                          <p className="mb-0 text-[14px]">
                            Remember your password?{" "}
                            <Link
                              to="/signin"
                              className="text-[#F97316] hover:underline"
                            >
                              Sign In
                            </Link>
                          </p>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

